import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Check, ExternalLink, Loader2, Plus, ReceiptText, Trash2, X, Banknote, Settings2, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SubmitClaimDialog } from '../components/reimbursements/SubmitClaimDialog';
import { ReimbursementPolicyCard } from '../components/reimbursements/ReimbursementPolicyCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  submitted: { label: 'Awaiting review', cls: 'bg-amber-500/15 text-amber-300' },
  approved: { label: 'Approved', cls: 'bg-sky-500/15 text-sky-300' },
  rejected: { label: 'Not approved', cls: 'bg-rose-500/15 text-rose-300' },
  paid: { label: 'Paid', cls: 'bg-emerald-500/15 text-emerald-300' },
};
const PAY_LABEL = { next_pay_run: 'next pay run', deposit_24h: 'deposit within 24h', processing_days: 'processing days' };
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const StatusPill = ({ s }) => <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS[s]?.cls || ''}`} data-testid={`reimb-status-${s}`}>{STATUS[s]?.label || s}</span>;

export default function Reimbursements() {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin = user?.role === 'admin' || user?.is_super_user;
  const [policy, setPolicy] = useState(null);
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState(isAdmin ? 'submitted' : 'all');
  const [open, setOpen] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [reject, setReject] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [busy, setBusy] = useState(null);
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exportStatus, setExportStatus] = useState('paid');
  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/reimbursements/export.csv`, { ...h, params: { month: exportMonth, status: exportStatus }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `reimbursements-${exportMonth}-${exportStatus}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Export failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([
        axios.get(`${API}/reimbursements/policy`, h),
        axios.get(`${API}/reimbursements`, { ...h, params: filter !== 'all' ? { status: filter } : {} }),
      ]);
      setPolicy(p.data); setItems(l.data.items || []);
      if (isAdmin) setSummary((await axios.get(`${API}/reimbursements/summary`, h)).data);
    } catch { setItems([]); }
  }, [token, filter]); // eslint-disable-line
  useEffect(() => { if (token) load(); }, [load, token]);

  const act = async (id, action, body) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/reimbursements/${id}/${action}`, body || {}, h);
      if (action === 'approve') {
        toast({ title: 'Claim approved', description: r.data.accounting_emailed ? 'Accounting has been emailed with the receipt.' : (r.data.accounting_email_missing ? 'No accounting email is set — add one under Policy so the bookkeeper gets notified.' : 'Accounting email could not be sent; the claim is still approved.') });
      } else if (action === 'reject') toast({ title: 'Claim not approved', description: 'The worker has been notified.' });
      else if (action === 'mark-paid') toast({ title: 'Marked as paid' });
      setReject(null); setRejectNote('');
      await load();
    } catch (e) {
      toast({ title: 'Action failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(null); }
  };
  const remove = async (id) => {
    setBusy(id);
    try { await axios.delete(`${API}/reimbursements/${id}`, h); toast({ title: 'Claim withdrawn' }); await load(); }
    catch (e) { toast({ title: 'Could not withdraw', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setBusy(null); }
  };
  const openReceipt = async (id) => {
    try { const r = await axios.get(`${API}/reimbursements/${id}/receipt`, h); window.open(r.data.url, '_blank', 'noopener'); }
    catch (e) { toast({ title: 'No receipt', description: e.response?.data?.detail || 'Could not open the receipt.', variant: 'destructive' }); }
  };

  if (!user) return null;
  if (user.role === 'viewer') return <Navigate to="/home" replace />;
  const canSubmit = policy?.enabled_for_me;
  const filters = isAdmin ? [['submitted', 'Awaiting review'], ['approved', 'Approved'], ['paid', 'Paid'], ['rejected', 'Not approved'], ['all', 'All']] : [['all', 'All'], ['submitted', 'Awaiting review'], ['approved', 'Approved'], ['paid', 'Paid']];

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="reimbursements-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><ReceiptText className="w-5 h-5 text-[#1E88E5]" /> Reimbursements</h1>
          <p className="text-sm mt-0.5 text-slate-400">
            {isAdmin ? 'Review receipts your team submits. Approving a claim emails accounting and queues the payout.' : 'Snap the receipt, pick what it was for, and submit. You\'ll be notified when it\'s approved and paid.'}
            {policy && <> Paid via <b className="text-slate-300">{PAY_LABEL[policy.payment_method] || policy.payment_method}</b>.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <Button variant="outline" onClick={() => setShowPolicy((v) => !v)} data-testid="reimb-policy-toggle"><Settings2 className="w-4 h-4 mr-1" /> Policy</Button>}
          {canSubmit && <Button onClick={() => setOpen(true)} className="bg-[#1E88E5] hover:bg-blue-700" data-testid="reimb-new-btn"><Plus className="w-4 h-4 mr-1" /> New claim</Button>}
        </div>
      </div>

      {policy && !canSubmit && !isAdmin && (
        <p className="text-sm text-amber-400 ht-card p-3" data-testid="reimb-disabled-note">Reimbursements aren't enabled for your account. Ask an administrator if you need to claim an authorized purchase.</p>
      )}

      {isAdmin && (
        <div className="ht-card p-3 flex flex-wrap items-center gap-2" data-testid="reimb-export">
          <Download className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-300">Accounting export</span>
          <input type="month" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }} data-testid="reimb-export-month" />
          <select value={exportStatus} onChange={(e) => setExportStatus(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }} data-testid="reimb-export-status">
            <option value="paid">Paid (by paid date)</option>
            <option value="paid,approved">Paid + approved (by expense date)</option>
            <option value="submitted,approved,paid,rejected">Everything (by expense date)</option>
          </select>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!exportMonth} data-testid="reimb-export-btn">Download CSV</Button>
          <span className="text-[11px] text-slate-500 ml-auto">One row per claim plus totals by worker and by type.</span>
        </div>
      )}

      {isAdmin && showPolicy && <ReimbursementPolicyCard token={token} policy={policy} onSaved={(p) => { setPolicy((old) => ({ ...old, ...p })); }} />}

      {isAdmin && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reimb-summary">
          {['submitted', 'approved', 'paid', 'rejected'].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`ht-card p-3 text-left ${filter === s ? 'ring-1 ring-[#1E88E5]' : ''}`} data-testid={`reimb-summary-${s}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{STATUS[s].label}</div>
              <div className="text-lg text-white font-semibold">{summary[s]?.count || 0} <span className="text-xs text-slate-400 font-normal">· {money(summary[s]?.amount)}</span></div>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="reimb-filters">
        {filters.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ background: filter === k ? '#1E88E5' : 'transparent', color: filter === k ? '#fff' : '#A0AEC0', border: `1px solid ${filter === k ? '#1E88E5' : '#2D3748'}` }}
            data-testid={`reimb-filter-${k}`}>{l}</button>
        ))}
      </div>

      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-[#1E88E5]" /> : items.length === 0 ? (
        <p className="text-sm text-slate-400 ht-card p-4" data-testid="reimb-empty">{filter === 'submitted' && isAdmin ? 'Nothing waiting for review.' : 'No claims here yet.'}</p>
      ) : (
        <div className="space-y-2" data-testid="reimb-list">
          {items.map((c) => (
            <div key={c.id} className="ht-card p-3 flex flex-wrap items-start gap-3" data-testid={`reimb-item-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium">{isAdmin ? `${c.user_name} · ` : ''}{c.category}</span>
                  <StatusPill s={c.status} />
                  {c.immediate_requested && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300">Immediate payment requested</span>}
                </div>
                <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-2">
                  <span>{c.date}</span>{c.vendor && <span>· {c.vendor}</span>}
                  {c.project_number && <span>· {c.project_number} {c.project_name}</span>}{c.job_number && <span>· Job {c.job_number}</span>}
                  {c.mileage_km ? <span>· {c.mileage_km} km @ {money(c.mileage_rate)} = {money(c.mileage_amount)}</span> : null}
                  {c.tax_amount ? <span>· tax {money(c.tax_amount)}</span> : null}
                </div>
                {c.note && <p className="text-xs text-slate-300 mt-1">{c.note}</p>}
                {c.reviewer_note && <p className="text-xs text-amber-300 mt-1">Reviewer: {c.reviewer_note}</p>}
                {c.status === 'paid' && c.paid_note && <p className="text-[11px] text-emerald-300 mt-1">Paid · {c.paid_note}</p>}
              </div>
              <div className="text-right">
                <div className="text-lg text-white font-semibold" data-testid={`reimb-total-${c.id}`}>{money(c.total)}</div>
                <div className="flex items-center gap-1 justify-end mt-1 flex-wrap">
                  {c.receipt_key && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openReceipt(c.id)} data-testid={`reimb-receipt-${c.id}`}><ExternalLink className="w-3 h-3 mr-1" /> Receipt</Button>}
                  {isAdmin && c.status === 'submitted' && (<>
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={busy === c.id} onClick={() => act(c.id, 'approve')} data-testid={`reimb-approve-${c.id}`}><Check className="w-3 h-3 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-rose-300 border-rose-700/60" disabled={busy === c.id} onClick={() => setReject(c)} data-testid={`reimb-reject-${c.id}`}><X className="w-3 h-3 mr-1" /> Reject</Button>
                  </>)}
                  {isAdmin && c.status === 'approved' && c.payment_method !== 'next_pay_run' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-300 border-emerald-700/60" disabled={busy === c.id} onClick={() => act(c.id, 'mark-paid')} data-testid={`reimb-paid-${c.id}`}><Banknote className="w-3 h-3 mr-1" /> Mark paid</Button>
                  )}
                  {isAdmin && c.status === 'approved' && c.payment_method === 'next_pay_run' && <span className="text-[11px] text-slate-400">Pays on next pay run</span>}
                  {((!isAdmin && c.status === 'submitted') || (isAdmin && c.status !== 'paid')) && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" disabled={busy === c.id} onClick={() => remove(c.id)} data-testid={`reimb-delete-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SubmitClaimDialog open={open} onClose={() => setOpen(false)} policy={policy} token={token}
        onSubmitted={() => { toast({ title: 'Claim submitted', description: 'An administrator will review it.' }); setFilter(isAdmin ? 'submitted' : 'all'); load(); }} />

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent data-testid="reimb-reject-dialog">
          <DialogHeader><DialogTitle>Not approving this claim</DialogTitle><DialogDescription>Tell {reject?.user_name} why. They'll see this note.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="e.g. Purchase wasn't pre-authorized / receipt unreadable" data-testid="reimb-reject-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!rejectNote.trim() || busy === reject?.id} onClick={() => act(reject.id, 'reject', { note: rejectNote })} data-testid="reimb-reject-confirm">Reject claim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
